import React, { useState, useMemo, useEffect } from 'react';
import { KANBAN_COLUMNS } from '../../utils/optimizationTransformers';
import KanbanCard from './KanbanCard';

const ALL_KANBAN_KEYS = KANBAN_COLUMNS.map((c) => c.key);

const COLUMN_COLORS = {
  blue: {
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    header: 'border-blue-500/30',
  },
  green: {
    badge: 'bg-green-500/20 text-green-300 border-green-500/30',
    header: 'border-green-500/30',
  },
  amber: {
    badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    header: 'border-amber-500/30',
  },
  gray: {
    badge: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    header: 'border-gray-500/30',
  },
};

const KanbanBoard = ({
  buckets,
  onCardClick,
  onRatingSuccess,
  isMobile,
  animatingCardIds = {},
  viewMode = 'board',
  /** Si se omite o es la lista completa, se muestran las 4 columnas. Si es un subconjunto, solo esas columnas. */
  visibleColumnKeys = ALL_KANBAN_KEYS,
}) => {
  const columns = useMemo(() => {
    const allow = new Set(
      Array.isArray(visibleColumnKeys) && visibleColumnKeys.length > 0
        ? visibleColumnKeys
        : ALL_KANBAN_KEYS
    );
    return KANBAN_COLUMNS.filter((c) => allow.has(c.key));
  }, [visibleColumnKeys]);

  const [activeTab, setActiveTab] = useState(() => columns[0]?.key ?? 'pendientes');

  useEffect(() => {
    setActiveTab((prev) => (columns.some((c) => c.key === prev) ? prev : columns[0]?.key ?? 'pendientes'));
  }, [columns]);

  const cardLayout = viewMode === 'list' ? 'row' : 'card';
  const columnGapClass = viewMode === 'list' ? 'space-y-2' : 'space-y-3';

  const desktopGridClass =
    columns.length <= 1
      ? 'grid-cols-1'
      : columns.length === 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : columns.length === 3
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  if (isMobile) {
    return (
      <div>
        {/* Tab bar */}
        <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-hide -mx-2 px-2">
          {columns.map((col) => {
            const count = (buckets[col.key] || []).length;
            const isActive = activeTab === col.key;
            const colors = COLUMN_COLORS[col.color];
            return (
              <button
                key={col.key}
                type="button"
                onClick={() => setActiveTab(col.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-white/15 text-white border border-white/20'
                    : 'bg-white/5 text-white/60 border border-transparent hover:bg-white/10'
                }`}
              >
                {col.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${colors.badge}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active column cards */}
        <div className={columnGapClass}>
          {(buckets[activeTab] || []).length === 0 ? (
            <div className="flex items-center justify-center py-12 text-white/40 text-sm">
              Sin optimizaciones en esta columna
            </div>
          ) : (
            (buckets[activeTab] || []).map((card) => (
              <KanbanCard
                key={card.id}
                card={card}
                columnKey={activeTab}
                layout={cardLayout}
                onClick={onCardClick}
                onRatingSuccess={onRatingSuccess}
                animatingCardIds={animatingCardIds}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  // Desktop: columnas visibles (1 a 4)
  return (
    <div className={`grid gap-4 ${desktopGridClass}`}>
      {columns.map((col) => {
        const cards = buckets[col.key] || [];
        const colors = COLUMN_COLORS[col.color];
        return (
          <div key={col.key} className="flex flex-col min-h-0">
            {/* Column header */}
            <div
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl bg-white/5 border-b-2 mb-3 ${colors.header}`}
            >
              <h3 className="text-xs font-bold uppercase tracking-wider text-white/80">
                {col.label}
              </h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors.badge}`}
              >
                {cards.length}
              </span>
            </div>

            {/* Column body */}
            <div
              className={`flex-1 overflow-y-auto max-h-[calc(100vh-260px)] pr-1 scrollbar-thin ${columnGapClass}`}
            >
              {cards.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-white/30 text-xs">
                  Sin optimizaciones
                </div>
              ) : (
                cards.map((card) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    columnKey={col.key}
                    layout={cardLayout}
                    onClick={onCardClick}
                    onRatingSuccess={onRatingSuccess}
                    animatingCardIds={animatingCardIds}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanBoard;
